<!-- Index -->
<dhp-index
  class="mb-8 w-full lg:mb-0 lg:w-auto print:hidden"
  #articleIndex
  [(isExpanded)]="isIndexExpanded"
  [(isHeaderHidden)]="isHeaderHidden"
  [indexItems]="indexItems()"
  [articleTitle]="articleDetails?.title || ''"
  [activeSection]="activeSection"
  [lang]="articleDetails?.magazine?.language ?? 'nl'"
  (indexItemClicked)="onIndexItemClicked($event)"
></dhp-index>
<div class="relative flex flex-col lg:flex-row">
  <!-- Main content -->
  <div
    class="w-full grow px-4 transition-all duration-300 ease-in-out sm:px-6 lg:px-8 xl:px-12 print:ml-0"
    [ngClass]="!hasIndex() ? '' : isIndexExpanded ? 'lg:ml-96' : 'lg:ml-16'"
  >
    <!-- Breadcrumb Navigation -->
    <div class="max-w-7xl container mx-auto">
      <div class="mt-6">
        <dhp-breadcrumbs
          class="print:hidden"
          [crumbs]="breadcrumbItems"
        ></dhp-breadcrumbs>
      </div>
    </div>

    <!--
      Outside the centred content container on purpose: the banner runs edge to
      edge of the content column, so it may not be capped by the container's
      max width, and the negative margins cancel this column's own padding.
    -->
    <dhp-advertisement
      class="-mx-4 sm:-mx-6 lg:-mx-8 xl:-mx-12 print:hidden"
      [referenceId]="articleDetails?.id ?? null"
      placement="article"
    />

    <div class="max-w-7xl container mx-auto flex min-h-screen flex-col">
      <!-- Content wrapper -->
      <div class="grow">
        <!-- Publication Info and Action Buttons -->
        <div
          class="flex w-full flex-col items-start justify-start gap-8 bg-white"
        >
          <!-- Publication Info and Action Buttons -->
          <div
            class="flex w-full flex-col border-b border-secondary-border py-4 print:hidden"
          >
            <!-- Mobile Layout -->
            <div class="flex flex-col lg:hidden">
              <!-- Action Buttons -->
              <div
                class="flex items-center justify-end gap-4 border-b border-secondary-border pb-4"
              >
                <dhp-article-action-buttons
                  [canAccess]="hasArticleAccess"
                  [canDownload]="canDownloadArticle"
                  [hasDownload]="!!articleDetails?.pdf"
                  [isLabeled]="false"
                  [loading]="loading"
                  (download)="onDownload()"
                  (print)="onPrint()"
                  (share)="onShare()"
                ></dhp-article-action-buttons>
              </div>
              <!-- Publication Info -->
              <div class="flex flex-col space-y-2 pt-4">
                <div class="flex items-center justify-between">
                  <div
                    class="text-base leading-normal font-normal text-secondary-text"
                  >
                    {{ 'pages.articles.publicationDate' | translate }}
                  </div>
                  <div
                    class="text-base leading-normal font-normal text-primary-text"
                  >
                    {{ articleDetails?.published_at | localizedDate }}
                  </div>
                </div>
                <div class="flex items-center justify-between">
                  <div
                    class="text-base leading-normal font-normal text-secondary-text"
                  >
                    {{ 'pages.articles.udhPublicationNumber' | translate }}
                  </div>
                  <div
                    class="text-base leading-normal font-normal text-primary-text"
                  >
                    {{ articleDetails?.udh_id ?? '-' }}
                  </div>
                </div>
                @if (articleDetails?.article_type; as articleType) {
                  <div class="flex items-center justify-between">
                    <div
                      class="text-base leading-normal font-normal text-secondary-text"
                    >
                      {{ 'pages.articles.articleType' | translate }}
                    </div>
                    <div
                      class="text-base leading-normal font-normal text-primary-text"
                    >
                      {{ 'articleTypes.' + articleType.name | translate }}
                    </div>
                  </div>
                }
              </div>
            </div>

            <!-- Desktop Layout -->
            <div
              class="hidden lg:flex lg:flex-row lg:items-center lg:justify-between"
            >
              <!-- Publication Info -->
              <div
                class="flex flex-col divide-y divide-secondary-border lg:flex-row lg:gap-8 lg:divide-y-0"
              >
                <!-- Publication Date -->
                <div
                  class="flex items-center justify-between py-3 lg:block lg:py-0"
                >
                  <div
                    class="text-sm leading-normal font-normal text-secondary-text"
                  >
                    {{ 'pages.articles.publicationDate' | translate }}
                  </div>
                  <div
                    class="text-base leading-normal font-normal text-primary-text"
                  >
                    {{ articleDetails?.published_at | localizedDate }}
                  </div>
                </div>
                <!-- UDH Number -->
                <div
                  class="flex items-center justify-between py-3 lg:block lg:py-0"
                >
                  <div
                    class="text-sm leading-normal font-normal text-secondary-text"
                  >
                    {{ 'pages.articles.udhPublicationNumber' | translate }}
                  </div>
                  <div
                    class="text-base leading-normal font-normal text-primary-text"
                  >
                    {{ articleDetails?.udh_id ?? '-' }}
                  </div>
                </div>
                @if (articleDetails?.article_type; as articleType) {
                  <!-- Article type -->
                  <div
                    class="flex items-center justify-between py-3 lg:block lg:py-0"
                  >
                    <div
                      class="text-sm leading-normal font-normal text-secondary-text"
                    >
                      {{ 'pages.articles.articleType' | translate }}
                    </div>
                    <div
                      class="text-base leading-normal font-normal text-primary-text"
                    >
                      {{ 'articleTypes.' + articleType.name | translate }}
                    </div>
                  </div>
                }
              </div>

              <!-- Action Buttons -->
              <dhp-article-action-buttons
                [canAccess]="hasArticleAccess"
                [canDownload]="canDownloadArticle"
                [hasDownload]="!!articleDetails?.pdf"
                [isLabeled]="true"
                [loading]="loading"
                (download)="onDownload()"
                (print)="onPrint()"
                (share)="onShare()"
              ></dhp-article-action-buttons>
            </div>
          </div>
        </div>

        <div
          class="mt-6 flex w-full flex-col gap-6 bg-white text-primary-text md:gap-8 lg:gap-10"
        >
          <!-- Article Title and Metadata -->
          <div class="flex w-full flex-col gap-4">
            <!-- Title -->
            <h1
              class="leading-tight font-medium"
              [ngClass]="getTitleSizeClass(articleDetails?.title)"
              [attr.lang]="articleDetails?.magazine?.language ?? 'nl'"
            >
              <span
                [innerHTML]="
                  articleDetails?.title | htmlContent | processContent
                "
              ></span>
              @if (getFootnoteReference('title'); as footnote) {
                <dhp-footnote-reference
                  [footnoteNumber]="footnote.number"
                  [content]="footnote.content"
                  (footnoteSelected)="scrollToElement($event)"
                />
              }
            </h1>

            @if (articleDetails; as details) {
              @if (details.subtitle) {
                <h2
                  class="leading-tight font-medium italic"
                  [ngClass]="getSubtitleSizeClass(details.title)"
                  [attr.lang]="details.magazine?.language ?? 'nl'"
                >
                  <span
                    [innerHTML]="
                      details.subtitle | htmlContent | processContent
                    "
                  ></span>
                  @if (getFootnoteReference('subtitle'); as footnote) {
                    <dhp-footnote-reference
                      [footnoteNumber]="footnote.number"
                      [content]="footnote.content"
                      (footnoteSelected)="scrollToElement($event)"
                    />
                  }
                </h2>
              }
            }

            <!-- Metadata Section -->
            <div
              class="inline-flex flex-wrap items-center justify-start gap-12"
            >
              @if (getArticle(); as article) {
                <!-- Authors -->
                <div class="flex items-start justify-start gap-6">
                  <div
                    class="inline-flex flex-col items-start justify-start gap-2"
                  >
                    <div class="inline-flex items-center justify-start gap-2.5">
                      <div
                        class="inline-flex flex-row items-start justify-start"
                      >
                        <dhp-article-meta-data
                          [article]="article"
                          [isSelectionAndEssence]="isSelectionAndEssence"
                          [showEditionAndDate]="false"
                        />
                        @if (getFootnoteReference('author'); as footnote) {
                          <dhp-footnote-reference
                            [footnoteNumber]="footnote.number"
                            [content]="footnote.content"
                            (footnoteSelected)="scrollToElement($event)"
                          />
                        }
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Article Content -->
        <div class="mt-8 w-full max-w-none overflow-visible">
          @if (initializationComplete) {
            <!-- Error Toast for Download -->
            <dhp-error-toast [message]="error" />
            @if (content && !error) {
              <div
                class="prose w-full min-w-full overflow-visible wrap-break-word"
                [innerHTML]="
                  content
                    | htmlContent
                    | processContent
                    | processFootnotes: articleDetails?.footnotes
                "
                [attr.lang]="articleDetails?.magazine?.language ?? 'nl'"
                (click)="handleFootnoteClick($event)"
                (keydown.enter)="handleFootnoteClick($event)"
                tabindex="0"
                role="article"
              ></div>
            }
            <!-- Action Buttons -->
            @if (hasArticleAccess) {
              <div
                class="mt-24 flex items-center justify-end gap-6 print:hidden"
              >
                <dhp-article-action-buttons
                  [canAccess]="hasArticleAccess"
                  [canDownload]="canDownloadArticle"
                  [hasDownload]="!!articleDetails?.pdf"
                  [isLabeled]="true"
                  [loading]="loading"
                  (download)="onDownload()"
                  (print)="onPrint()"
                  (share)="onShare()"
                ></dhp-article-action-buttons>
              </div>
            }
            <!-- Separator Line -->
            @if (hasArticleAccess) {
              <div class="mt-4 w-full border-b border-secondary-border"></div>
            }
            <!-- Footnotes Section -->
            <!-- TODO: Background should extend to edge -->
            @if (articleDetails; as details) {
              @if (details.footnotes.length && hasArticleAccess) {
                <div
                  class="footnotes mt-6 mb-16 bg-ocean-blue-50 p-4 md:px-10 md:pt-10 lg:px-20 lg:pb-20 print:p-4"
                >
                  <div class="py-6">
                    <h2
                      class="text-4xl leading-tight font-medium text-neutral-800"
                    >
                      {{ 'pages.articles.footnotes' | translate }}
                    </h2>
                  </div>
                  <div>
                    <!-- Footnotes -->
                    @for (
                      footnote of getAllFootnotes();
                      track footnote;
                      let i = $index
                    ) {
                      <div
                        class="group relative flex items-baseline gap-4 rounded px-4 py-4 transition-all duration-300 ease-in-out hover:bg-ocean-blue-75 active:bg-ocean-blue-75 print:p-0"
                        [id]="'footnote-' + (i + 1)"
                        [attr.aria-label]="
                          'pages.articles.goToFootnoteReference'
                            | translate: { number: i + 1 }
                        "
                        (click)="
                          handleFootnoteItemClick(
                            $event,
                            'footnote-ref-' + (i + 1)
                          )
                        "
                        (keydown.enter)="
                          handleFootnoteItemClick(
                            $event,
                            'footnote-ref-' + (i + 1)
                          )
                        "
                        tabindex="0"
                        role="button"
                      >
                        <div class="w-6 text-lg font-normal text-neutral-800">
                          {{ i + 1 }}
                        </div>
                        <div class="flex flex-1 flex-col gap-2 pr-12">
                          <div
                            class="prose lg:prose-lg"
                            [innerHTML]="
                              footnote.content | htmlContent | processContent
                            "
                            [lang]="details.magazine?.language ?? 'nl'"
                          ></div>
                        </div>
                        <div class="footnote-arrow">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <line x1="12" y1="19" x2="12" y2="5"></line>
                            <polyline points="5 12 12 5 19 12"></polyline>
                          </svg>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              }
            }
          }
        </div>

        @if (articleDetails && isAuthCheckCompleted && !hasArticleAccess) {
          <div
            class="mt-8 mb-16 flex w-full flex-col items-center bg-primary-background print:hidden"
            id="further-reading"
          >
            <div
              class="flex w-full flex-col items-start gap-5 rounded-lg border border-primary-border bg-primary-background p-6"
            >
              <div class="flex w-full flex-col gap-4">
                <h3
                  class="text-xl leading-tight font-medium text-primary-border sm:text-2xl"
                >
                  {{ 'pages.articles.readFurther' | translate }}
                </h3>
                @if (articleDetails!.magazine; as magazine) {
                  <div
                    class="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div class="min-w-0 flex-1">
                      <p
                        class="text-sm font-medium tracking-wide text-secondary-text uppercase"
                      >
                        {{ 'pages.articles.subscribeOptionTitle' | translate }}
                      </p>
                      <p class="mt-2 text-base leading-normal sm:text-lg">
                        {{ subscriptionButtonDetail }}
                      </p>
                    </div>
                    <div
                      class="flex w-full flex-col gap-3 sm:w-auto sm:shrink-0 sm:items-end sm:self-center"
                    >
                      <dhp-subscribe-button
                        [magazineId]="magazine.id"
                        [canPurchaseArticle]="canPurchaseArticle()"
                        [userAccessLevel]="articleDetails!.user_access"
                        (getDetailedInfo)="subscriptionButtonDetail = $event"
                      ></dhp-subscribe-button>
                    </div>
                  </div>
                }

                @if (canPurchaseArticle()) {
                  <div
                    class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div class="flex min-w-0 flex-1 flex-col gap-2">
                      <p
                        class="text-sm font-medium tracking-wide text-secondary-text uppercase"
                      >
                        {{ 'pages.articles.buyArticleOptionTitle' | translate }}
                      </p>
                      <p class="text-base leading-normal sm:text-lg">
                        {{
                          'pages.articles.buyArticleDescriptionShort'
                            | translate
                        }}
                      </p>
                      @if (getArticlePurchaseTotal(); as totalPrice) {
                        @if (
                          hasStudentArticlePrice() &&
                            getArticleStudentPurchaseTotal();
                          as studentPrice
                        ) {
                          <dl
                            class="flex flex-col gap-1 text-sm text-secondary-text"
                          >
                            <!--
                              A recognised student is only shown the rate they
                              will actually pay; the regular rate would just be
                              noise. Everyone else sees both, so an unrecognised
                              student can spot the discount and contact us.
                            -->
                            @if (!studentPricingApplies()) {
                              <div
                                class="flex flex-wrap items-baseline gap-x-2"
                              >
                                <dt>
                                  {{
                                    'pages.articles.regularPriceLabel'
                                      | translate
                                  }}
                                </dt>
                                <dd class="font-medium text-primary-text">
                                  {{ totalPrice | currency: 'EUR' }}
                                </dd>
                                <dd>
                                  {{
                                    'pages.articles.includingVatLabel'
                                      | translate
                                  }}
                                </dd>
                              </div>
                            }
                            <div class="flex flex-wrap items-baseline gap-x-2">
                              <dt>
                                {{
                                  'pages.articles.studentPriceOptionLabel'
                                    | translate
                                }}
                              </dt>
                              <dd class="font-medium text-primary-text">
                                {{ studentPrice | currency: 'EUR' }}
                              </dd>
                              <dd>
                                {{
                                  'pages.articles.includingVatLabel' | translate
                                }}
                              </dd>
                            </div>
                          </dl>
                        } @else {
                          <div
                            class="flex flex-wrap items-center gap-x-1 gap-y-1 text-sm text-secondary-text"
                          >
                            <span>
                              {{
                                'pages.articles.buyingPriceDetails'
                                  | translate
                                    : {
                                        price: (totalPrice | currency: 'EUR'),
                                      }
                              }}
                            </span>
                          </div>
                        }
                      }
                    </div>
                    @if (getArticlePurchaseTotal(); as totalPrice) {
                      <button
                        class="btn-primary btn w-full sm:w-auto"
                        (click)="goToPurchaseCheckout()"
                        type="button"
                      >
                        @if (
                          hasStudentArticlePrice() && !studentPricingApplies()
                        ) {
                          <!--
                            Two rates are listed above, so naming one of them on
                            the button would contradict the other. Whenever a
                            single rate is shown, the button names it.
                          -->
                          {{ 'pages.articles.buyArticle' | translate }}
                        } @else if (
                          studentPricingApplies() &&
                            getArticleStudentPurchaseTotal();
                          as studentPrice
                        ) {
                          {{
                            'pages.articles.buyArticleForPrice'
                              | translate
                                : { price: (studentPrice | currency: 'EUR') }
                          }}
                        } @else {
                          {{
                            'pages.articles.buyArticleForPrice'
                              | translate
                                : { price: (totalPrice | currency: 'EUR') }
                          }}
                        }
                      </button>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Related Publications Section (always at bottom) -->
      <!-- <dhp-related-articles [articleDetails]="articleDetails" class="mt-12 print:hidden"></dhp-related-articles> -->
    </div>
  </div>
</div>
